import React, { useState, useEffect, useCallback } from 'react';
import API from './config';

const COULEURS = {
  primaire: '#1E3A5F',
  vert: '#16A34A',
  rouge: '#DC2626',
  jaune: '#F59E0B',
  texte: '#1F2937',
  fond: '#F7F8FA',
};

const MODES_LABEL = {
  virement: { label: 'Virement', emoji: '💳', color: '#1E3A5F' },
  cheque: { label: 'Chèque', emoji: '📝', color: '#7A4E2D' },
  especes: { label: 'Espèces', emoji: '💵', color: '#16A34A' },
};

function moisActuelBornes() {
  const d = new Date();
  const debut = new Date(d.getFullYear(), d.getMonth(), 1);
  const fin = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return [debut.toISOString().slice(0, 10), fin.toISOString().slice(0, 10)];
}

function moisPrecedentBornes() {
  const d = new Date();
  const debut = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  const fin = new Date(d.getFullYear(), d.getMonth(), 0);
  return [debut.toISOString().slice(0, 10), fin.toISOString().slice(0, 10)];
}

function anneeActuelleBornes() {
  const d = new Date();
  return [`${d.getFullYear()}-01-01`, `${d.getFullYear()}-12-31`];
}

function formaterDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formaterMontant(s) {
  const n = parseFloat(s || 0);
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

export default function LivreRecettes({ apiFetch, onImprimer }) {
  const [moisDebut, moisFin] = moisActuelBornes();
  const [dateFrom, setDateFrom] = useState(moisDebut);
  const [dateTo, setDateTo] = useState(moisFin);
  const [onglet, setOnglet] = useState('toutes'); // 'toutes' | 'especes'
  const [entrees, setEntrees] = useState([]);
  const [totaux, setTotaux] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur] = useState(null);

  const charger = useCallback(async () => {
    setLoading(true);
    setErreur(null);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('from', dateFrom);
      if (dateTo) params.set('to', dateTo);
      const qs = params.toString();
      const [rEntrees, rTotaux] = await Promise.all([
        apiFetch(`${API}/livre-recettes/${qs ? '?' + qs : ''}`),
        apiFetch(`${API}/livre-recettes/totaux${qs ? '?' + qs : ''}`),
      ]);
      if (!rEntrees.ok || !rTotaux.ok) throw new Error('Erreur de chargement');
      const dEntrees = await rEntrees.json();
      const dTotaux = await rTotaux.json();
      setEntrees(dEntrees.entrees || []);
      setTotaux(dTotaux);
    } catch (e) {
      setErreur(e.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, dateFrom, dateTo]);

  useEffect(() => { charger(); }, [charger]);

  const entreesAffichees = onglet === 'especes'
    ? entrees.filter(e => e.registre_especes)
    : entrees;

  const setPeriode = (bornes) => { setDateFrom(bornes[0]); setDateTo(bornes[1]); };

  const exporterExcel = async () => {
    setLoading(true);
    setErreur(null);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('from', dateFrom);
      if (dateTo) params.set('to', dateTo);
      const qs = params.toString();
      const r = await apiFetch(`${API}/livre-recettes/export.xlsx${qs ? '?' + qs : ''}`);
      if (!r.ok) throw new Error('Erreur lors de la génération du fichier Excel');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const suffixe = (dateFrom ? `_${dateFrom}` : '') + (dateTo ? `-${dateTo}` : '');
      a.download = `livre-recettes${suffixe}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setErreur(e.message || 'Erreur export Excel');
    } finally {
      setLoading(false);
    }
  };

  const imprimerPDF = () => {
    if (!onImprimer) return;
    onImprimer({
      entrees: entreesAffichees,
      totaux,
      periode: { from: dateFrom || null, to: dateTo || null },
    });
  };

  const s = {
    page: { padding: '20px', background: COULEURS.fond, minHeight: 'calc(100vh - 80px)' },
    h1: { color: COULEURS.primaire, fontSize: '24px', fontWeight: '700', margin: '0 0 6px 0' },
    sousTitre: { color: '#6B7280', fontSize: '13px', marginBottom: '20px' },
    card: { background: 'white', borderRadius: '10px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginBottom: '16px' },
    filtreRow: { display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' },
    label: { fontSize: '13px', fontWeight: '600', color: COULEURS.texte },
    input: { padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '14px' },
    btn: (active = false, color = COULEURS.primaire) => ({
      background: active ? color : 'white',
      color: active ? 'white' : color,
      border: `1px solid ${color}`,
      padding: '7px 14px',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: '600',
    }),
    onglets: { display: 'flex', gap: '0', borderBottom: '2px solid #E5E7EB', marginBottom: '12px' },
    onglet: (active) => ({
      padding: '10px 16px',
      cursor: 'pointer',
      borderBottom: active ? `3px solid ${COULEURS.primaire}` : '3px solid transparent',
      color: active ? COULEURS.primaire : '#6B7280',
      fontWeight: active ? '700' : '500',
      fontSize: '14px',
      background: 'transparent',
      border: 'none',
      marginBottom: '-2px',
    }),
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
    th: { textAlign: 'left', padding: '10px 8px', borderBottom: `2px solid ${COULEURS.primaire}`, color: COULEURS.primaire, fontWeight: '700', fontSize: '12px', textTransform: 'uppercase' },
    td: { padding: '10px 8px', borderBottom: '1px solid #E5E7EB', verticalAlign: 'top' },
    badge: (bg, color) => ({ display: 'inline-block', padding: '3px 8px', borderRadius: '4px', background: bg, color, fontSize: '11px', fontWeight: '600' }),
    montantPositif: { color: COULEURS.vert, fontWeight: '700', fontVariantNumeric: 'tabular-nums' },
    montantNegatif: { color: COULEURS.rouge, fontWeight: '700', fontVariantNumeric: 'tabular-nums' },
    totauxGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' },
    statCard: (color) => ({ padding: '12px 14px', borderLeft: `4px solid ${color}`, background: '#F9FAFB', borderRadius: '6px' }),
    statLabel: { fontSize: '11px', textTransform: 'uppercase', color: '#6B7280', fontWeight: '600' },
    statMontant: { fontSize: '20px', fontWeight: '700', color: COULEURS.texte, marginTop: '4px', fontVariantNumeric: 'tabular-nums' },
    statSub: { fontSize: '11px', color: '#9CA3AF', marginTop: '2px' },
  };

  const nbEspeces = entrees.filter(e => e.registre_especes).length;

  return (
    <div style={s.page}>
      <h1 style={s.h1}>📒 Livre des recettes</h1>
      <p style={s.sousTitre}>
        Registre chronologique des encaissements et remboursements (art. L123-28 CCom, BOI-BIC-DECLA-30-10-10).
      </p>

      <div style={s.card}>
        <div style={s.filtreRow}>
          <span style={s.label}>📅 Période :</span>
          <input type="date" style={s.input} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          <span style={{ color: '#6B7280' }}>au</span>
          <input type="date" style={s.input} value={dateTo} onChange={e => setDateTo(e.target.value)} />
          <div style={{ display: 'flex', gap: '6px', marginLeft: '8px' }}>
            <button style={s.btn(false)} onClick={() => setPeriode(moisActuelBornes())}>Mois en cours</button>
            <button style={s.btn(false)} onClick={() => setPeriode(moisPrecedentBornes())}>Mois précédent</button>
            <button style={s.btn(false)} onClick={() => setPeriode(anneeActuelleBornes())}>Année en cours</button>
            <button style={s.btn(false)} onClick={() => { setDateFrom(''); setDateTo(''); }}>Tout</button>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
            <button style={s.btn(false, '#16A34A')} onClick={exporterExcel} disabled={loading || entrees.length === 0} title="Exporter en Excel (3 feuilles)">
              📥 Excel
            </button>
            <button style={s.btn(false, '#7A4E2D')} onClick={imprimerPDF} disabled={loading || entrees.length === 0} title="Aperçu et impression PDF">
              🖨️ PDF
            </button>
            <button style={s.btn(true)} onClick={charger} disabled={loading}>
              {loading ? '⏳' : '🔄'} Rafraîchir
            </button>
          </div>
        </div>
      </div>

      {totaux && (
        <div style={s.card}>
          <h3 style={{ ...s.label, fontSize: '15px', marginBottom: '12px' }}>Récapitulatif</h3>
          <div style={s.totauxGrid}>
            <div style={s.statCard(COULEURS.primaire)}>
              <div style={s.statLabel}>Total général</div>
              <div style={s.statMontant}>{formaterMontant(totaux.total_general)}</div>
              <div style={s.statSub}>{totaux.nb_paiements} encaissement(s) − {totaux.nb_remboursements_avoirs} remboursement(s)</div>
            </div>
            {Object.entries(totaux.par_mode || {}).map(([mode, val]) => {
              const m = MODES_LABEL[mode] || { label: mode, emoji: '', color: '#6B7280' };
              return (
                <div key={mode} style={s.statCard(m.color)}>
                  <div style={s.statLabel}>{m.emoji} {m.label}</div>
                  <div style={s.statMontant}>{formaterMontant(val.montant)}</div>
                  <div style={s.statSub}>{val.nb_operations} opération(s)</div>
                </div>
              );
            })}
            <div style={s.statCard(COULEURS.jaune)}>
              <div style={s.statLabel}>📋 Registre espèces</div>
              <div style={s.statMontant}>{formaterMontant(totaux.registre_especes?.total)}</div>
              <div style={s.statSub}>{totaux.registre_especes?.nb_operations} opération(s) consignée(s)</div>
            </div>
          </div>
        </div>
      )}

      <div style={s.card}>
        <div style={s.onglets}>
          <button style={s.onglet(onglet === 'toutes')} onClick={() => setOnglet('toutes')}>
            📒 Toutes les recettes ({entrees.length})
          </button>
          <button style={s.onglet(onglet === 'especes')} onClick={() => setOnglet('especes')}>
            📋 Registre espèces ({nbEspeces})
          </button>
        </div>

        {erreur && (
          <div style={{ padding: '12px', background: '#FEF2F2', color: '#991B1B', borderRadius: '6px', marginBottom: '12px' }}>
            ⚠️ {erreur}
          </div>
        )}

        {loading && entreesAffichees.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>Chargement…</div>
        ) : entreesAffichees.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
            Aucune entrée pour cette période{onglet === 'especes' ? ' en espèces' : ''}.
          </div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Date</th>
                <th style={s.th}>Type</th>
                <th style={s.th}>Pièce</th>
                <th style={s.th}>Client / Affaire</th>
                <th style={s.th}>Mode</th>
                <th style={s.th}>Détails</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Montant</th>
              </tr>
            </thead>
            <tbody>
              {entreesAffichees.map((e, i) => {
                const m = MODES_LABEL[e.mode_paiement] || { label: e.mode_paiement, emoji: '', color: '#6B7280' };
                const estEncaissement = e.type === 'encaissement';
                const montant = parseFloat(e.montant);
                return (
                  <tr key={i}>
                    <td style={s.td}>{formaterDate(e.date)}</td>
                    <td style={s.td}>
                      {estEncaissement
                        ? <span style={s.badge('#DCFCE7', '#166534')}>↗ Encaissement</span>
                        : <span style={s.badge('#FEE2E2', '#991B1B')}>↘ Remboursement</span>}
                    </td>
                    <td style={s.td}>
                      {e.piece?.type === 'facture' ? (
                        <span><strong>F.</strong> {e.piece.numero || '—'}</span>
                      ) : (
                        <span>
                          <strong>Av.</strong> {e.piece?.numero || '—'}
                          {e.piece?.facture_origine_numero && (
                            <span style={{ color: '#6B7280', fontSize: '11px' }}> / sur F. {e.piece.facture_origine_numero}</span>
                          )}
                        </span>
                      )}
                    </td>
                    <td style={s.td}>
                      <div style={{ fontWeight: '600' }}>{e.client?.nom || '—'}</div>
                      <div style={{ fontSize: '11px', color: '#6B7280' }}>{e.affaire?.intitule || ''}</div>
                    </td>
                    <td style={s.td}>
                      <span style={s.badge('white', m.color)}>{m.emoji} {m.label}</span>
                      {e.registre_especes && (
                        <div style={{ ...s.badge('#FEF3C7', '#92400E'), marginTop: '4px', fontSize: '10px' }}>📋 Registre</div>
                      )}
                    </td>
                    <td style={s.td}>
                      {e.mode_paiement === 'cheque' && (
                        <div style={{ fontSize: '11px', color: '#4B5563' }}>
                          {e.numero_cheque && <div>N° {e.numero_cheque}</div>}
                          {e.banque_cheque && <div>{e.banque_cheque}</div>}
                          {e.tireur_cheque && <div style={{ color: '#9CA3AF' }}>Tireur : {e.tireur_cheque}</div>}
                        </div>
                      )}
                      {e.reference && e.mode_paiement !== 'cheque' && (
                        <div style={{ fontSize: '11px', color: '#4B5563' }}>Réf : {e.reference}</div>
                      )}
                      {e.notes && <div style={{ fontSize: '11px', color: '#9CA3AF', fontStyle: 'italic' }}>{e.notes}</div>}
                    </td>
                    <td style={{ ...s.td, textAlign: 'right' }}>
                      <span style={montant >= 0 ? s.montantPositif : s.montantNegatif}>
                        {formaterMontant(e.montant)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
